import type { AiScribeStep } from "../types";

/**
 * Steps during which a live el8-transcribe socket/worker exists. While in any of
 * these, in-flight or buffered transcription work would be lost if the worker
 * were hard-terminated (terminating the worker closes its socket with code 1001
 * "Going Away", after which el8-transcribe times out the stream).
 *
 * Includes `paused` (the socket stays open across a pause so recording can
 * resume) and `finishing` (a graceful close is already in flight).
 *
 * Note: `paused` also appears in `STEPS_IDLE` in `step.ts`. These are
 * orthogonal axes — `STEPS_IDLE` means "safe for the user to change settings",
 * while this set means "the WebSocket worker is still live". A paused recording
 * is both: UI-idle (no audio flowing) but socket-live (the worker is kept open
 * for resume). The dual membership is intentional.
 */
const STEPS_SCRIBE_SOCKET_LIVE = new Set<AiScribeStep>([
  "recordingInitializing",
  "recordingActive",
  "paused",
  "finishing",
]);

export function isScribeSocketLiveStep(step: AiScribeStep): boolean {
  return STEPS_SCRIBE_SOCKET_LIVE.has(step);
}

export type ScribeTeardownAction = "graceful-close" | "terminate";

/**
 * Decides how to dispose the scribe client when the owning component unmounts.
 * A live recording must be flushed gracefully (`close()`, which lets buffered
 * audio transcribe and the session finalize) rather than hard-terminated.
 */
export function resolveScribeTeardownAction(
  step: AiScribeStep,
): ScribeTeardownAction {
  return isScribeSocketLiveStep(step) ? "graceful-close" : "terminate";
}

export type ScribePipelineAction = "noop" | "setup" | "teardown";

/**
 * Decides what the setup effect should do when its inputs (microphone, device
 * list, options) change. While a recording is live we must do nothing —
 * reconfiguring tears the pipeline down and recreates the scribe client, which
 * kills the worker mid-session. Otherwise we (re)build when inputs are ready and
 * tear down when they are not.
 */
export function resolveScribePipelineAction(params: {
  step: AiScribeStep;
  microphoneDeviceId: string | null;
  hasDevices: boolean;
  hasOptions: boolean;
}): ScribePipelineAction {
  const { step, microphoneDeviceId, hasDevices, hasOptions } = params;

  if (isScribeSocketLiveStep(step)) return "noop";
  if (!microphoneDeviceId || !hasDevices || !hasOptions) return "teardown";
  return "setup";
}
